import * as PIXI from 'pixi.js';
import { LayoutContainer } from '@pixi/layout/components'; // eslint-disable-line
import TappableGraphicsButton from './TappableGraphicsButton';

export const COLOR_ORDER = ['Red', 'Green', 'Blue', 'Purple', 'Yellow', 'Black'];

const COLOR_FILLS = {
    Red: new PIXI.FillGradient({
        type: 'linear', start: { x: 0, y: 0 }, end: { x: 0, y: 1 },
        colorStops: [{ offset: 0, color: '#cd091f' }, { offset: 1, color: '#850614' }],
    }),
    Green: new PIXI.FillGradient({
        type: 'linear', start: { x: 0, y: 0 }, end: { x: 0, y: 1 },
        colorStops: [{ offset: 0, color: '#10875f' }, { offset: 1, color: '#0b583e' }],
    }),
    Blue: new PIXI.FillGradient({
        type: 'linear', start: { x: 0, y: 0 }, end: { x: 0, y: 1 },
        colorStops: [{ offset: 0, color: '#2375af' }, { offset: 1, color: '#174c72' }],
    }),
    Purple: new PIXI.FillGradient({
        type: 'linear', start: { x: 0, y: 0 }, end: { x: 0, y: 1 },
        colorStops: [{ offset: 0, color: '#914494' }, { offset: 1, color: '#5e2c60' }],
    }),
    Yellow: new PIXI.FillGradient({
        type: 'linear', start: { x: 0, y: 0 }, end: { x: 0, y: 1 },
        colorStops: [{ offset: 0, color: '#efe15a' }, { offset: 1, color: '#efe15a' }],
    }),
    Black: new PIXI.FillGradient({
        type: 'linear', start: { x: 0, y: 0 }, end: { x: 0, y: 1 },
        colorStops: [{ offset: 0, color: '#333333' }, { offset: 1, color: '#222222' }],
    }),
};

function colorButtonStyle(size, fill, selected) {
    const scale = size / 125;
    return {
        width: size,
        height: size,
        backgroundColor: '#1a1a1a',
        borderColor: '#141c2a',
        innerBorderWidth: 8 * scale,
        borderWidth: 6 * scale,
        cornerRadius: size / 2,
        fill,
        ...(selected ? { outerBorderColor: '#79d1ff' } : {}),
    };
}

export function cardMatchesColorFilter(card, selectedColors) {
    if (!selectedColors || selectedColors.size === 0) return true;
    const colors = card.color;
    if (!colors) return false;
    if (typeof colors === 'string') return selectedColors.has(colors);
    return colors.some((color) => selectedColors.has(color));
}

function replaceButton(previous, button, parent) {
    if (previous?.parent) {
        const index = previous.parent.getChildIndex(previous);
        previous.parent.addChildAt(button, index);
        previous.destroy();
        return;
    }
    parent.addChild(button);
}

export default class ColorFiltersRow extends LayoutContainer {
    constructor({
        width,
        height = 130,
        buttonSize = 125,
        paddingLeft = 50,
        paddingRight = 50,
        gap = 16,
        multiple = true,
        selected = [],
        onChange = null,
    } = {}) {
        super();

        this.label = 'colorFiltersRow';
        this.buttonSize = buttonSize;
        this.multiple = multiple;
        this.onChange = onChange;
        this.selected = new Set(selected);
        this.buttons = {};
        this.layout = {
            width,
            height,
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap,
            paddingLeft,
            paddingRight,
        };

        this.refresh();
    }

    setSelected(selected) {
        this.selected = new Set(selected);
        this.refresh();
    }

    toggle(color) {
        if (this.selected.has(color)) {
            this.selected.delete(color);
        } else if (this.multiple) {
            this.selected.add(color);
        } else {
            this.selected.clear();
            this.selected.add(color);
        }
        this.refresh();
        this.onChange?.(this.selected);
    }

    refresh() {
        for (const color of COLOR_ORDER) {
            this.updateButton(color);
        }
    }

    updateButton(color) {
        const previous = this.buttons[color];
        const button = new TappableGraphicsButton(
            colorButtonStyle(this.buttonSize, COLOR_FILLS[color], this.selected.has(color)),
        );
        button.onButtonTap = () => this.toggle(color);
        replaceButton(previous, button, this);
        this.buttons[color] = button;
    }
}
